import React, { useMemo, useCallback } from 'react'
import { Checkbox } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import actionTypes from '../../../../constants/actionTypes'
import { formatNum } from '../../../../utils/utils'
import { useAddPhotosTrack } from '../hooks/useAddPhotosTrack'
import './HeaderExtra.scss'

interface HeaderExtraProps {
  sameWatermarkOnly?: boolean
  currentWatermarkKey?: string | null
  onToggleSameWatermark?: (checked: boolean) => void
}

const HeaderExtra: React.FC<HeaderExtraProps> = ({
  sameWatermarkOnly = false,
  currentWatermarkKey,
  onToggleSameWatermark
}) => {
  const dispatch = useAppDispatch()
  const photoState = useAppSelector((state) => state.photo)
  const photosNum = photoState.photosNum
  const photos = photoState.photos
  const checkAll = photoState.checkAll
  const checkedPhotos = photoState.checkedPhotos
  const unCheckedPhotos = photoState.unCheckedPhotos
  const filter = photoState.filter
  const groupId = useAppSelector((state) => state.group.groupId)
  // 获取已选中的照片（从 photoPicker 中）
  const selectedPhotosFromPicker = useAppSelector((state) => {
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }
    return []
  })

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  // 检查照片是否有防伪码
  const hasAntiFakeCode = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      // extension 可能是字符串或对象
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 过滤出符合筛选条件的照片（hasWatermark = true && mediaType = 0），并排除已选中的照片，同时排除 watermarkBaseID = "21" 的照片，以及没有防伪码的照片
  const filteredPhotos = useMemo(() => {
    if (!photos || photos.length === 0) {
      return photos
    }
    // 创建已选中照片的 smallURL Set，用于快速查找
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )
    return photos.filter((photo: any) => {
      // 基础筛选条件
      const hasWatermark = photo.hasWatermark === true
      const isImage = photo.mediaType === 0
      const notSelected = !selectedSmallURLs.has(photo.smallURL)

      // 排除 watermarkBaseID = "21" 的照片
      const photoWatermarkBaseID = photo.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'

      // 检查是否有防伪码
      const hasAntiFake = hasAntiFakeCode(photo)

      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    })
  }, [photos, selectedPhotosFromPicker, hasAntiFakeCode])

  // 计算筛选后的选中数量
  const selectedCount = useMemo(() => {
    if (checkAll) {
      // 全选状态：筛选后的照片数量减去未选中的数量
      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      return filteredPhotos.filter((p: any) => !unCheckedSeqSet.has(p.seq))
        .length
    } else {
      // 非全选状态：已选中的照片中，符合筛选条件的数量
      const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
      return filteredPhotos.filter((p: any) => checkedSeqSet.has(p.seq)).length
    }
  }, [checkAll, filteredPhotos, checkedPhotos, unCheckedPhotos])

  // 全选
  const onCheckAll = (e: any) => {
    const checked = e.target.checked

    // 埋点：全选/取消全选
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: checked ? 'chooseAll' : 'unChooseAll',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    dispatch({
      type: actionTypes.SET_CHECK_ALL,
      data: checked
    })
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
  }

  // 获取 unCheckedPhotos 的 seq Set
  const unCheckedSeqSet = useMemo(
    () => new Set(Object.keys(unCheckedPhotos).map(Number)),
    [unCheckedPhotos]
  )

  // 获取 checkedPhotos 的 seq Set
  const checkedSeqSet = useMemo(
    () => new Set(Object.keys(checkedPhotos).map(Number)),
    [checkedPhotos]
  )

  return (
    <div className="add-batch-header-extra">
      <div className="add-batch-header-extra__left">
        <Checkbox
          disabled={filteredPhotos.length === 0}
          indeterminate={
            (checkAll &&
              filteredPhotos.some((p: any) => unCheckedSeqSet.has(p.seq))) ||
            (!checkAll &&
              filteredPhotos.some((p: any) => checkedSeqSet.has(p.seq)))
          }
          checked={
            checkAll &&
            filteredPhotos.length > 0 &&
            filteredPhotos.every((p: any) => !unCheckedSeqSet.has(p.seq))
          }
          onChange={onCheckAll}
        >
          全选
        </Checkbox>
        <span className="add-batch-header-extra__info">
          已选择{' '}
          <strong className="add-batch-header-extra__info-count">
            {formatNum(selectedCount)}
          </strong>{' '}
          张 / 共{' '}
          <strong className="add-batch-header-extra__info-count">
            {formatNum(filteredPhotos.length)}
          </strong>{' '}
          张
        </span>
      </div>
      <div className="add-batch-header-extra__right">
        {currentWatermarkKey ? (
          <Checkbox
            checked={sameWatermarkOnly}
            onChange={(e) => onToggleSameWatermark?.(e.target.checked)}
          >
            添加相同水印
          </Checkbox>
        ) : null}
      </div>
    </div>
  )
}

export default HeaderExtra
